import { useCallback, useState } from 'react';
import { Icon } from '@blueprintjs/core';
import { Button, Dialog, DialogTrigger, IconButton, Modal, ModalOverlay } from '@launchpad-ui/components';
import { type Team } from 'launchdarkly-api-typescript';

import { type CustomRole, type Member } from '../model';

import { PolicyDisplay, RoleAttributesTable, RoleCarousel } from './CustomRoles/components';
import {
  type ConsolidationInfo,
  createResolvedRole,
  extractRoleAttributesForMember,
  extractTeamRoleAttributes,
  findMissingRoleAttributes,
  hasRoleAttributePlaceholders,
} from './CustomRoles/utils';

import './CustomRoleDetails.scss';
import './CustomRoles/components/CustomRoleDetails.scss';
import './CustomRoles/components/modal-scrolling.scss';
import './styles.scss';

type Props = {
  role?: CustomRole;
  roleKey?: string;
  roles?: CustomRole[];
  roleType?: 'direct' | 'team' | 'consolidated';
  member?: Member;
  teamRoleAttributes?: { [key: string]: string[] };
  teamData?: Array<{ role: CustomRole; team: Team }>;
  consolidationInfo?: ConsolidationInfo;
  onTeamDataNeeded?: (teamKey: string) => void;
  teamKey?: string;
  showTeamRoleCallout?: boolean;
  /** When true, the role badge is highlighted to show it matches the active role filter. */
  isMatch?: boolean;
  /** When true, the role badge is de-emphasized (a filter is active and this role is not a match). */
  isDimmed?: boolean;
};

const TeamRoleCallout = () => (
  <div className="customRoleDetails__infoBox">
    <div className="customRoleDetails__infoBoxHeader">
      <Icon icon="info-sign" iconSize={16} />
      <div className="customRoleDetails__infoBoxContent">
        <p>
          Team roles are evaluated per member using their direct and team role attributes. This view is not a
          member-specific effective permissions check.
        </p>
      </div>
    </div>
  </div>
);

export const CustomRoleDetails = ({
  role,
  member,
  roles,
  roleType,
  teamRoleAttributes,
  onTeamDataNeeded,
  teamKey,
  showTeamRoleCallout,
  isMatch,
  isDimmed,
}: Props) => {
  const [activeView, setActiveView] = useState<'none' | 'resolved'>('none');
  const initialIndex = roles && role ? roles.findIndex((r) => r?._id === role._id) : 0;
  const [currentRoleIndex, setCurrentRoleIndex] = useState<number>(initialIndex > -1 ? initialIndex : 0);

  const toggleResolvedRole = useCallback(() => {
    setActiveView((prevView) => (prevView === 'resolved' ? 'none' : 'resolved'));
  }, []);

  // Carousel navigation handlers
  const handlePrevious = useCallback(() => {
    setCurrentRoleIndex((prev) => Math.max(0, prev - 1));
    setActiveView('none');
  }, []);

  const handleNext = useCallback(() => {
    if (roles) {
      setCurrentRoleIndex((prev) => Math.min(roles.length - 1, prev + 1));
      setActiveView('none');
    }
  }, [roles]);

  const currentRole = roles && roles.length > 0 ? roles[currentRoleIndex] : role;
  const isMultipleRoles = roles && roles.length > 1;

  const roleTypeLabel = roleType === 'team' ? 'Team Role' : 'Direct Role';

  // Hooks must run unconditionally, so this is called before the
  // currentRole guard below.
  const handleModalOpen = useCallback(() => {
    if (roleType === 'team' && !teamRoleAttributes && onTeamDataNeeded && teamKey) {
      onTeamDataNeeded(teamKey);
    }
  }, [roleType, teamRoleAttributes, onTeamDataNeeded, teamKey]);

  if (!currentRole) {
    return <div>No role data available</div>;
  }

  const hasPlaceholders = hasRoleAttributePlaceholders(currentRole);

  const roleAttributes = teamRoleAttributes
    ? extractTeamRoleAttributes(teamRoleAttributes, currentRole)
    : extractRoleAttributesForMember(member ?? ({} as Member), currentRole);

  const roleWithPreset = currentRole as CustomRole & { baseRole?: string; basePermissions?: string };

  const isPresetRole = !!roleWithPreset._presetStatements;

  const combinedPolicy = [...(currentRole.policy || []), ...(roleWithPreset._presetStatements || [])];

  const cleanRole = {
    _id: currentRole._id,
    key: currentRole.key,
    name: currentRole.name,
    ...(currentRole.description && { description: currentRole.description }),
    policy: combinedPolicy,
    ...(roleWithPreset.baseRole && { baseRole: roleWithPreset.baseRole }),
    ...(roleWithPreset.basePermissions && { basePermissions: roleWithPreset.basePermissions }),
  };

  const resolvedRole = createResolvedRole(currentRole, member ?? ({} as Member), teamRoleAttributes);
  const resolvedRoleWithExtra = resolvedRole as CustomRole & { baseRole?: string; basePermissions?: string };
  const cleanResolvedRole = {
    _id: resolvedRole._id,
    key: resolvedRole.key,
    name: resolvedRole.name,
    ...(resolvedRole.description && { description: resolvedRole.description }),
    policy: resolvedRole.policy,
    ...(resolvedRoleWithExtra._presetStatements && { _presetStatements: resolvedRoleWithExtra._presetStatements }),
    ...(roleWithPreset.baseRole && { baseRole: roleWithPreset.baseRole }),
    ...(roleWithPreset.basePermissions && { basePermissions: roleWithPreset.basePermissions }),
  };

  return (
    <DialogTrigger onOpenChange={(isOpen) => isOpen && handleModalOpen()}>
      <Button
        key={role?._id || currentRole._id}
        variant="primary"
        className={`customRoleTag${isMatch ? ' customRoleTag--match' : ''}${isDimmed ? ' customRoleTag--dimmed' : ''}`}
        size="small"
      >
        {role?.name || currentRole.name}
      </Button>
      <ModalOverlay>
        <Modal size="large" variant="drawer" className="custom-role-modal-scrollable">
          <Dialog className="customRoleDrawer" aria-label="Custom role details">
            <IconButton
              icon="cancel"
              size="medium"
              variant="minimal"
              className="closeDrawer"
              slot="close"
              aria-label="Close drawer"
            />

            <div className="customRoleDetails__content">
              <div className="customRoleDetails__heading">
                <div className="customRoleDetails__title">
                  <span className="customRoleDetails__titleLabel">
                    {isPresetRole ? 'Preset Role:' : 'Custom Role:'}
                  </span>
                  <span className="customRoleDetails__titleValue">{` ${currentRole.name}`}</span>
                </div>
                <div className="customRoleDetails__details">Role Key: {currentRole.key}</div>
              </div>

              {showTeamRoleCallout && roleType === 'team' && (
                <div className="customRoleDetails__section">
                  <TeamRoleCallout />
                </div>
              )}

              {isMultipleRoles && (
                <div className="customRoleDetails__carouselContainer">
                  <RoleCarousel
                    currentIndex={currentRoleIndex}
                    totalRoles={roles!.length}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    roleTypeLabel={roleTypeLabel}
                  />
                </div>
              )}

              {/* Policy Display */}
              <PolicyDisplay
                title={activeView === 'resolved' ? 'Resolved Role (with actual resource values)' : 'Policy'}
                policy={activeView === 'resolved' ? cleanResolvedRole : cleanRole}
                baseRole={roleWithPreset.baseRole}
                showBaseRole={!!roleWithPreset.baseRole}
                className="customRoleDetails__policyContainer"
              />

              {hasPlaceholders && (
                <div className="customRoleDetails__attributesSection">
                  <div className="customRoleDetails__attributesHeader">
                    <h3>Role Attributes</h3>
                    {hasPlaceholders && (
                      <div className="customRoleDetails__toggleContainer">
                        <Button
                          variant={activeView === 'resolved' ? 'primary' : 'default'}
                          onPress={toggleResolvedRole}
                        >
                          {activeView === 'resolved' ? 'Hide Resolved Role' : 'Show Resolved Role'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <RoleAttributesTable
                    availableAttributes={Object.fromEntries(roleAttributes.map((attr) => [attr.name, attr.values]))}
                    missingAttributes={findMissingRoleAttributes(
                      currentRole.policy,
                      Object.fromEntries(roleAttributes.map((attr) => [attr.name, attr.values])),
                    )}
                  />
                </div>
              )}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
